import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Link, useSearchParams} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {toast} from 'react-toastify';
import {Check, CheckCircle} from '../../icons';
import {Spinner} from '../../Spinner';
import {LoadErrorState} from '../../elements/LoadErrorState';
import {ClientAvatar, clientIconUrl} from '../../elements/ClientAvatar';
import {LocalStorage} from '../../../utils/LocalStorage';
import {isOk} from '../../../utils/apiResponse';
import {isPlanLimitError, publishAdAccountQuota, quotaFromLinkError} from '../../../utils/adAccountQuota';
import {planLimitText, recordQuotaEvent} from '../../client/control_central/accountQuota';
import {REPORT_TYPE_AD_ACCOUNT_NOT_MONITORED} from '../../../reports';
import {formatCompactMoney} from '../../client/panel_v11/panelFormatters';
import {DISMISS_ACCOUNT, DISMISS_CAMPAIGN, UncontrolledSpendApi} from '../../../api/uncontrolledSpendApi';
import {UncoveredSpendApi} from '../../../api/uncoveredSpendApi';
import {campaignKey} from '../../elements/uncoveredSpend';
import {unmarkResolved} from '../../elements/uncoveredResolutions';
import {PARAM_CLIENT, PARAM_FILTER, myAlertsPath} from '../alertsCenterPaths';
import {UncontrolledItem} from './UncontrolledItem';
import {DismissedBlock, IdleBlock, NoClientBlock} from './UncontrolledBlocks';
import {
    FILTER_ALL, FILTER_NO_LINE, FILTER_STALE, FILTER_UNCOVERED, addableCount, adjustedTotals, filterCounts,
    filterFromParam, isAllClear, itemKey, visibleClients,
} from './uncontrolledModel';
import {joinNames, relativeTime} from './uncontrolledText';

const RAW = {interpolation: {escapeValue: false}};
const FILTER_DOTS = {[FILTER_NO_LINE]: '-no-line', [FILTER_UNCOVERED]: '-uncovered', [FILTER_STALE]: '-stale'};
// Cada código del backend con su texto (el `error_message` crudo puede ser el `detail` de DRF). Los de
// `STALE_CODES` dicen que la pantalla quedó vieja (otra persona ya lo resolvió, una campaña dejó de
// existir): reintentar no sirve, así que además se recarga el informe.
// El tope del plan (`CANT_ADD_MORE_AD_ACCOUNTS`) no está acá: lo dice `planLimitText`, con sus números.
const ERROR_KEYS = {
    LINE_USES_CONDITION: 'line_uses_condition',
    ITEM_NOT_FOUND: 'item_not_found',
    CAMPAIGN_NOT_FOUND: 'campaign_not_found',
    MISSING_CAMPAIGNS: 'missing_campaigns',
    CAMPAIGNS_NOT_SYNCED: 'campaigns_not_synced',
    NO_RECIPIENTS: 'no_recipients',
    NOTE_TOO_LONG: 'note_too_long',
    NOT_SENT: 'not_sent',
    ERROR_NOT_SNOOZED: 'not_snoozed',
};
// `CAMPAIGNS_NOT_SYNCED` no recarga: las campañas existen, y recargar dejaría la tarjeta igual.
const STALE_CODES = ['ITEM_NOT_FOUND', 'CAMPAIGN_NOT_FOUND', 'MISSING_CAMPAIGNS', 'ERROR_NOT_SNOOZED'];
// La pantalla en los eventos del cupo (`account_quota_rejected_uncontrolled`).
const QUOTA_SURFACE = 'uncontrolled';

function rowKey(row) {
    return `${row.channel}|${row.account_id}`;
}

/** ¿Le llega el resumen por mail a quien mira? `null` si no se pudo saber: el botón no se dibuja. */
function summarySubscriptionOf(response) {
    if (!isOk(response) || !Array.isArray(response.data)) return null;
    return response.data.includes(REPORT_TYPE_AD_ACCOUNT_NOT_MONITORED);
}

/**
 * «Recibir este resumen por mail cada mañana» (tanda «Control de líneas», 15/10): el mismo interruptor
 * que «Informes» de «Mis alertas», desde la pantalla donde se lee el resumen. Optimista: si no se guardó,
 * vuelve a como estaba y lo avisa. Si no se pudo leer el estado, no se ofrece (queda el link a «Mis
 * alertas» de la nota).
 */
function SummarySubscription({userId}) {
    const {t} = useTranslation();
    const [subscribed, setSubscribed] = useState(null);
    const [saving, setSaving] = useState(false);
    const mounted = useRef(true);

    useEffect(() => {
        mounted.current = true;
        if (!userId) return () => { mounted.current = false; };
        UncontrolledSpendApi.reportSubscription(userId).then((response) => {
            if (mounted.current) setSubscribed(summarySubscriptionOf(response));
        });
        return () => { mounted.current = false; };
    }, [userId]);

    const change = (next) => {
        setSubscribed(next);
        setSaving(true);
        UncontrolledSpendApi.setReportSubscription(userId, next).then((response) => {
            if (!mounted.current) return;
            setSaving(false);
            if (isOk(response)) return;
            setSubscribed(!next);
            toast.error(t('alerts_center.uncontrolled.subscribe.error'));
        });
    };

    if (subscribed === null) return null;
    if (subscribed) {
        return (
            <p className="alc-unc-mail -on" data-testid="alc-unc-subscribe">
                <Check size={14} weight="bold" aria-hidden="true"/>
                <span>{t('alerts_center.uncontrolled.subscribe.on')}</span>
                <span aria-hidden="true">·</span>
                <button type="button" className="alc-link -button" disabled={saving} aria-busy={saving || undefined}
                        onClick={() => change(false)}>
                    {t('alerts_center.uncontrolled.subscribe.off_action')}
                </button>
            </p>
        );
    }
    return (
        <div className="alc-unc-mail" data-testid="alc-unc-subscribe">
            <button type="button" className="alc-btn -ghost" disabled={saving} aria-busy={saving || undefined}
                    onClick={() => change(true)}>
                {t('alerts_center.uncontrolled.subscribe.cta')}
            </button>
        </div>
    );
}

/**
 * «Inversión sin control» — la solapa `/alerts/sin-control` (mockup aprobado, frente del 05/10 tras la
 * capacitación de Not Ordinary). Desde UD-5 la abre toda la 2.1: con el Centro de Alertas es una de sus
 * solapas (`AlertsCenterContent`); sin él se ve sola (`UncontrolledSoloPage`).
 *
 * La plata que ninguna línea de seguimiento mira, para alguien como una directora: el total arriba, por
 * qué pasa, una tarjeta por cliente con su equipo y, en cada cuenta, la salida de un clic (sumar las
 * campañas a la línea, crear una línea, avisarle al equipo, «No hace falta controlarla»).
 *
 * `?cliente=<id>` (UD-5; lo arma la tarjeta de la ficha y el mail) pide el informe de ESE cliente: lo acota
 * el backend (`client_id`), la pantalla no filtra nada. Un chip lo dice («Sólo X») con «Ver todos los
 * clientes»; si el backend no devuelve el nombre (un id que quien mira no ve), sólo el segundo.
 *
 * Lo que el usuario resuelve se descuenta en pantalla (no se vuelve a pedir el informe por cada acción);
 * la próxima carga trae el dato del backend.
 */
export function UncontrolledSpendView() {
    const {t} = useTranslation();
    const [searchParams, setSearchParams] = useSearchParams();
    const filter = filterFromParam(searchParams.get(PARAM_FILTER));
    const clientId = searchParams.get(PARAM_CLIENT) || null;
    const myUserId = LocalStorage.fetchUserId() || null;
    const mounted = useRef(true);
    // Cada carga lleva un número: si dos se cruzan (dos «Volver a mostrar» seguidos, o cambiar de cliente
    // con la carga en vuelo), gana la última.
    const loadSeq = useRef(0);
    // El cliente de la URL, leído por `load` en el momento del pedido: así `load` no cambia de identidad y
    // la recarga que sigue a una acción pide el mismo informe que se ve.
    const clientRef = useRef(clientId);
    clientRef.current = clientId;

    const [report, setReport] = useState(null);
    // `null` = sin error; string = la carga falló (patrón de LoadErrorState).
    const [loadError, setLoadError] = useState(null);
    const [resolved, setResolved] = useState({});
    const [dismissedCampaigns, setDismissedCampaigns] = useState({});
    const [flagged, setFlagged] = useState({});
    const [rowsResolved, setRowsResolved] = useState({});

    useEffect(() => {
        mounted.current = true;
        return () => { mounted.current = false; };
    }, []);

    /**
     * Pide el informe. La carga de la pantalla, si falla, la reemplaza por `LoadErrorState`. El refresco
     * que sigue a una acción (`{refresh: true}`), si falla, conserva lo que se ve y lo avisa con un toast:
     * la acción salió bien y no corresponde tapar la pantalla. Con el informe nuevo se olvida lo anotado
     * en pantalla (resueltos, descartes, avisos): lo trae el backend, y dejarlo hacía que una cuenta
     * restaurada volviera dibujada como «No se muestra más» y que el total se descontara dos veces.
     */
    const load = useCallback(({refresh = false} = {}) => {
        const seq = ++loadSeq.current;
        return UncontrolledSpendApi.get({clientId: clientRef.current}).then((response) => {
            if (!mounted.current || seq !== loadSeq.current) return;
            if (!isOk(response) || !response.result) {
                if (refresh) toast.error(t('alerts_center.uncontrolled.refresh_error'));
                else setLoadError(t('alerts_center.uncontrolled.load_error'));
                return;
            }
            setReport(response.result);
            setLoadError(null);
            setResolved({});
            setDismissedCampaigns({});
            setFlagged({});
            setRowsResolved({});
        });
        // `t` cambia de identidad entre renders de i18next.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Al entrar y al cambiar de cliente (la pantalla no se remonta): el informe anterior no queda a la vista.
    useEffect(() => {
        setReport(null);
        setLoadError(null);
        load();
    }, [clientId, load]);

    const retry = () => {
        setLoadError(null);
        setReport(null);
        load();
    };

    // El filtro conserva el cliente, y «Ver todos los clientes» conserva el filtro.
    const setFilter = (next) => setSearchParams({
        ...(clientId ? {[PARAM_CLIENT]: clientId} : {}),
        ...(next === FILTER_ALL ? {} : {[PARAM_FILTER]: next}),
    });
    const clearClient = () => setSearchParams(filter === FILTER_ALL ? {} : {[PARAM_FILTER]: filter});
    const markItem = (setter, key, value) => setter((current) => ({...current, [key]: value}));
    const failed = (response) => {
        const code = response && (response.error_code || response.status);
        // El tope del plan, con sus números y el mismo texto que el resto de las pantallas (UD-15).
        if (isPlanLimitError(response)) {
            recordQuotaEvent('rejected', QUOTA_SURFACE);
            const quota = quotaFromLinkError(response);
            toast.error(planLimitText(t, quota));
            publishAdAccountQuota(quota);
            return false;
        }
        toast.error(t(`alerts_center.uncontrolled.error.${ERROR_KEYS[code] || 'generic'}`));
        if (STALE_CODES.includes(code)) return load({refresh: true}).then(() => false);
        return false;
    };

    // ------------------------------------------------------------------ acciones ---------------
    const actions = useMemo(() => ({
        // La línea la elige quien mira (`item.lines`): lo resuelto la recuerda para nombrarla y linkearla.
        // Si el backend sumó MENOS de las que la tarjeta promete (campañas que el sync todavía no trajo),
        // el ítem no está resuelto entero: se avisa y se recarga el informe, que trae lo que falta con su
        // monto. Marcarlo resuelto descontaba del total plata que sigue sin control (C-cs-fe-04).
        addToLine: (client, item, line) => UncontrolledSpendApi.addAllToLine(line.bucc_id).then((response) => {
            if (!isOk(response)) return failed(response);
            const key = itemKey(client.client_id, item);
            const added = ((response.result || {}).added_campaign_ids || []).length;
            if (added < addableCount(item, dismissedCampaigns[key])) {
                toast.warning(added > 0
                    ? t('alerts_center.uncontrolled.added_partial', {count: added, line: line.name, ...RAW})
                    : t('alerts_center.uncontrolled.error.campaigns_not_synced'));
                return load({refresh: true}).then(() => true);
            }
            markItem(setResolved, key, {kind: 'added', count: added, line});
            return true;
        }),
        createLine: (client, item) => UncontrolledSpendApi.createLine({
            clientId: client.client_id, channel: item.channel, accountId: item.account_id,
        }).then((response) => {
            if (!isOk(response)) return failed(response);
            const data = response.data || response.result || {};
            const lineId = (data.created_business_unit_ids || [])[0] || null;
            markItem(setResolved, itemKey(client.client_id, item), {kind: 'created', lineId});
            return true;
        }),
        dismissAccount: (client, item, reason) => UncontrolledSpendApi.dismiss({
            indicator: DISMISS_ACCOUNT, channel: item.channel, accountId: item.account_id, reason,
        }).then((response) => {
            if (!isOk(response)) return failed(response);
            markItem(setResolved, itemKey(client.client_id, item), {kind: 'dismissed', reason});
            return true;
        }),
        undoDismissAccount: (client, item) => UncontrolledSpendApi.undoDismiss({
            indicator: DISMISS_ACCOUNT, channel: item.channel, accountId: item.account_id,
        }).then((response) => {
            if (!isOk(response)) return failed(response);
            markItem(setResolved, itemKey(client.client_id, item), null);
            return true;
        }),
        // Una campaña se descarta sin elegir motivo: no se le inventa uno («La controla otra persona…»).
        dismissCampaign: (client, item, campaignId) => UncontrolledSpendApi.dismiss({
            indicator: DISMISS_CAMPAIGN, channel: item.channel, accountId: item.account_id, campaignId,
        }).then((response) => {
            if (!isOk(response)) return failed(response);
            const key = itemKey(client.client_id, item);
            setDismissedCampaigns((current) => ({...current, [key]: {...(current[key] || {}), [campaignId]: true}}));
            return true;
        }),
        undoDismissCampaign: (client, item, campaignId) => UncontrolledSpendApi.undoDismiss({
            indicator: DISMISS_CAMPAIGN, channel: item.channel, accountId: item.account_id, campaignId,
        }).then((response) => {
            if (!isOk(response)) return failed(response);
            const key = itemKey(client.client_id, item);
            setDismissedCampaigns((current) => {
                const next = {...(current[key] || {})};
                delete next[campaignId];
                return {...current, [key]: next};
            });
            return true;
        }),
        notify: (client, item, userIds, note) => UncontrolledSpendApi.notify({
            clientId: client.client_id, channel: item.channel, accountId: item.account_id, userIds, note,
        }).then((response) => {
            if (!isOk(response)) return failed(response);
            const result = response.result || {};
            markItem(setFlagged, itemKey(client.client_id, item), {...(result.flagged || {}), mine: true});
            return true;
        }),
        createLineForRow: (row) => UncontrolledSpendApi.createLine({
            clientId: row.client_id, channel: row.channel, accountId: row.account_id,
        }).then((response) => {
            if (!isOk(response)) return failed(response);
            markItem(setRowsResolved, rowKey(row), 'created');
            return true;
        }),
        dismissIdle: (row) => UncontrolledSpendApi.dismiss({
            indicator: DISMISS_ACCOUNT, channel: row.channel, accountId: row.account_id, reason: 'NOT_USED',
        }).then((response) => {
            if (!isOk(response)) return failed(response);
            markItem(setRowsResolved, rowKey(row), 'dismissed');
            return true;
        }),
        undoIdle: (row) => UncontrolledSpendApi.undoDismiss({
            indicator: DISMISS_ACCOUNT, channel: row.channel, accountId: row.account_id,
        }).then((response) => {
            if (!isOk(response)) return failed(response);
            markItem(setRowsResolved, rowKey(row), null);
            return true;
        }),
        // «Volver a mostrar» desde «Lo que dejaste de mostrar»: se recarga el informe, así la cuenta (o la
        // campaña) vuelve a su tarjeta con el monto de hoy y sale de la lista. Como el «Deshacer» del toast
        // del chip de la línea, borra la marca local de esa campaña y el detalle cacheado: si no, al volver
        // al Tablero en la misma pestaña el chip la seguía sacando (C-cs-fe-08).
        restore: (row) => UncontrolledSpendApi.undoDismiss({
            indicator: row.campaign_id ? DISMISS_CAMPAIGN : DISMISS_ACCOUNT, channel: row.channel,
            accountId: row.account_id, campaignId: row.campaign_id || null,
        }).then((response) => {
            if (!isOk(response)) return failed(response);
            UncoveredSpendApi.clearCache();
            if (row.campaign_id) {
                unmarkResolved([campaignKey({channel: row.channel, account_id: row.account_id,
                    campaign_id: row.campaign_id})]);
            }
            // La fila sigue ocupada hasta que vuelve la recarga: si no, un segundo clic llegaba al
            // backend con el silencio ya borrado.
            return load({refresh: true}).then(() => true);
        }),
        // `failed` y `t` no cambian lo que hacen las acciones entre renders.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }), [dismissedCampaigns]);

    const totals = useMemo(() => adjustedTotals(report, resolved, dismissedCampaigns),
        [report, resolved, dismissedCampaigns]);
    const counts = useMemo(() => filterCounts(report), [report]);
    const clients = useMemo(() => visibleClients(report, filter), [report, filter]);

    // ------------------------------------------------------------------ dibujo -----------------
    if (loadError !== null) {
        return (
            <LoadErrorState title={loadError} text={t('alerts_center.uncontrolled.load_error_explanation')}
                            retryLabel={t('alerts_center.uncontrolled.retry')} onRetry={retry}/>
        );
    }
    if (!report) return <div className="box-loader"><Spinner color="positive"/></div>;

    const currency = report.currency;
    const allClear = isAllClear(report, totals);

    const renderSummary = () => (
        <section className="alc-card alc-unc-summary" data-testid="alc-unc-summary">
            <p className="alc-unc-summary__label">{t('alerts_center.uncontrolled.summary.label')}</p>
            <p className="alc-unc-summary__total">≈ {formatCompactMoney(currency, totals.uncontrolled) || `${currency} 0`}</p>
            <p className="alc-unc-summary__lede">{t('alerts_center.uncontrolled.summary.lede')}</p>
            {totals.limit_only > 0 && (
                <p className="alc-unc-summary__second">
                    {t('alerts_center.uncontrolled.summary.limit_only', {amount: formatCompactMoney(currency, totals.limit_only), ...RAW})}
                </p>
            )}
            <p className="alc-unc-summary__foot">
                {t('alerts_center.uncontrolled.summary.currency_note', {currency})}
                {report.computed_on && ` · ${t('alerts_center.uncontrolled.summary.updated', {when: relativeTime(report.computed_on, t), ...RAW})}`}
                {report.totals.unconverted_count > 0
                    && ` · ${t('alerts_center.uncontrolled.summary.unconverted', {count: report.totals.unconverted_count})}`}
            </p>
            {report.snapshot_is_stale && (
                <p className="alc-notice -warn">{t('alerts_center.uncontrolled.summary.stale')}</p>
            )}
        </section>
    );

    const scopeName = report.scope && report.scope.client_name;
    // Con `?cliente=`, el «Todo bajo control» habla de ESE cliente. Sin nombre (un id que no existe o que
    // quien mira no ve) es neutro: no confirma ni niega que el cliente exista (C-cs-fe-05).
    const clearText = () => {
        if (!clientId) return t('alerts_center.uncontrolled.clear.text');
        if (scopeName) return t('alerts_center.uncontrolled.clear.text_client', {client: scopeName, ...RAW});
        return t('alerts_center.uncontrolled.clear.text_unknown_client');
    };
    const renderScope = () => (
        <div className="alc-unc-scope" data-testid="alc-unc-scope">
            {scopeName && (
                <span className="alc-unc-scope__chip">
                    {t('alerts_center.uncontrolled.client_filter', {client: scopeName, ...RAW})}
                </span>
            )}
            <button type="button" className="alc-link -button" onClick={clearClient}>
                {t('alerts_center.uncontrolled.client_filter_clear')}
            </button>
        </div>
    );

    const renderFilters = () => (
        <div className="alc-unc-why">
            <span className="alc-unc-why__label" id="alc-unc-why-label">{t('alerts_center.uncontrolled.why')}</span>
            <div className="uiseg" role="tablist" aria-labelledby="alc-unc-why-label">
                {[FILTER_ALL, FILTER_NO_LINE, FILTER_UNCOVERED, FILTER_STALE]
                    .filter((key) => key === FILTER_ALL || counts[key] > 0)
                    .map((key) => (
                        <button key={key} type="button" role="tab" aria-selected={filter === key}
                                className={`uiseg__btn${filter === key ? ' -active' : ''}`} onClick={() => setFilter(key)}>
                            {FILTER_DOTS[key] && <span className={`alc-unc-dot ${FILTER_DOTS[key]}`} aria-hidden="true"/>}
                            {t(`alerts_center.uncontrolled.filter.${key}`)}
                            <span className="uiseg__count">{counts[key]}</span>
                        </button>
                    ))}
            </div>
        </div>
    );

    const renderClient = (client) => (
        <section key={client.client_id} className="alc-card alc-unc-client" data-testid="alc-unc-client"
                 aria-label={client.client_name}>
            <header className="alc-unc-client__head">
                <ClientAvatar id={client.client_id} name={client.client_name} logoUrl={clientIconUrl(client)}
                              className="alc-unc-client__avatar"/>
                <span className="alc-unc-client__who">
                    <Link className="alc-unc-client__name" to={`/client/${client.client_id}`}>{client.client_name}</Link>
                    {client.team.length > 0 && (
                        <span className="alc-unc-client__team">
                            {t('alerts_center.uncontrolled.team', {names: joinNames(client.team.map((member) => member.name), t), ...RAW})}
                        </span>
                    )}
                </span>
                <span className="alc-unc-client__total">
                    {formatCompactMoney(currency, client.total_converted)}
                    <span className="alc-unc-client__period">{t('alerts_center.uncontrolled.in_7_days')}</span>
                </span>
            </header>
            {client.items.map((item) => {
                const key = itemKey(client.client_id, item);
                return (
                    <UncontrolledItem key={key} client={client} item={item} agencyCurrency={currency} myUserId={myUserId}
                                      state={{resolved: resolved[key], dismissedCampaigns: dismissedCampaigns[key],
                                              flagged: flagged[key]}}
                                      actions={actions}/>
                );
            })}
        </section>
    );

    return (
        <div className="alc-unc" data-testid="alc-unc">
            <p className="alc-unc__intro">{t('alerts_center.uncontrolled.intro')}</p>
            {clientId && renderScope()}
            {allClear ? (
                <section className="alc-card alc-unc-clear" data-testid="alc-unc-clear">
                    <CheckCircle size={22} weight="bold"/>
                    <div>
                        <h2 className="alc-card__title">{t('alerts_center.uncontrolled.clear.title')}</h2>
                        <p className="alc-unc-block__lede">{clearText()}</p>
                    </div>
                </section>
            ) : (
                <>
                    {renderSummary()}
                    {counts[FILTER_ALL] > 0 && renderFilters()}
                    {clients.map(renderClient)}
                </>
            )}
            <IdleBlock rows={report.idle} count={report.counts.idle} unmeasuredCount={report.counts.idle_unmeasured}
                       resolved={rowsResolved} actions={actions}/>
            <NoClientBlock block={report.no_client}/>
            <DismissedBlock rows={report.dismissed || []} agencyCurrency={currency} actions={actions}
                            count={(report.counts.dismissed_accounts || 0) + (report.counts.dismissed_campaigns || 0)}/>
            {report.counts.limit_only_accounts > 0 && (
                <p className="alc-unc__note">{t('alerts_center.uncontrolled.limit_only_note', {count: report.counts.limit_only_accounts})}</p>
            )}
            <p className="alc-unc__note">
                {t('alerts_center.uncontrolled.mail_note')}{' '}
                <Link className="alc-link" to={myAlertsPath(myUserId)}>{t('alerts_center.uncontrolled.mail_link')}</Link>
            </p>
            <SummarySubscription userId={myUserId}/>
        </div>
    );
}

export default UncontrolledSpendView;
